/**
 * Optional integration with the Pyramida notification bot (@pyramida/bot).
 *
 * When `VITE_NOTIFY_API_BASE` is configured AND the app runs inside Telegram
 * with a connected wallet, we hand the bot the signed `WebApp.initData` plus a
 * short-lived backend session minted from TON Connect `ton_proof`.
 */
import {
  isTelegramMiniApp,
  buildPartnerTrackingLink,
  buildReferralLink,
  buildTeamTelegramReferralLink,
  HAS_REAL_BOT,
  getIncomingReferrerKey,
  sanitizeReferrerKey,
} from './referral';

const NOTIFY_API_BASE: string = import.meta.env.VITE_NOTIFY_API_BASE?.replace(/\/+$/, '') || '';

/** Whether the notification-bot linking is configured for this deployment. */
export const NOTIFY_ENABLED = NOTIFY_API_BASE.length > 0;

/**
 * The traffic SOURCE label stamped into Story share links and the story QR
 * (`{ref_key}_{source}` /start grammar). The bot records each distinct account
 * arriving with it, which is what story-share quests count per sharer.
 */
export const STORY_SHARE_SOURCE = 'storyshare';

/**
 * Public URL of the bot-rendered 9:16 referral story image for `refKey`, used
 * as the media in `Telegram.WebApp.shareToStory`. Telegram downloads it
 * server-side, so it must be an absolute HTTPS URL. Null when the bot base
 * isn't configured.
 */
export function buildStoryImageUrl(
  refKey: number,
  prizeLabel?: string,
  bot: 'main' | 'team' = 'main',
  tagSource = true,
): string | null {
  if (!NOTIFY_ENABLED) return null;
  // Cache-bust per share: Telegram caches story media by URL, so a fresh `v`
  // each time forces it to re-fetch the latest image (live prize pool / tweaks).
  const label = prizeLabel ? `&label=${encodeURIComponent(prizeLabel)}` : '';
  // `bot=team` makes the embedded QR point at the AI recruiter bot's deep link.
  const botParam = bot === 'team' ? '&bot=team' : '';
  // Tag the QR's /start payload with the Story traffic source so scans are
  // attributable to the sharer's story (source-gated quests count them).
  const srcParam = bot === 'team' || !tagSource ? '' : `&src=${STORY_SHARE_SOURCE}`;
  return `${NOTIFY_API_BASE}/story.png?key=${encodeURIComponent(String(refKey))}${label}${botParam}${srcParam}&v=${Date.now()}`;
}

/**
 * Whether the running Telegram client supports `shareToStory` (Bot API 7.8+).
 * Returns false outside Telegram or on older clients.
 */
export function canShareToStory(): boolean {
  const tg = window.Telegram?.WebApp;
  if (!tg || typeof tg.shareToStory !== 'function') return false;
  return typeof tg.isVersionAtLeast === 'function' ? tg.isVersionAtLeast('7.8') : false;
}

/**
 * Whether an active story quest currently exists, so story shares are worth
 * tagging with the Story traffic source. Cached for the page lifetime; any
 * failure means "no quest" — links then carry only the plain ref key.
 */
let storyQuestActive: Promise<boolean> | null = null;
function hasActiveStoryQuest(): Promise<boolean> {
  storyQuestActive ??= fetchPublicQuests()
    .then((quests) => (quests ?? []).some((q) => q.type === 'story' || q.type === 'story_starts'))
    .catch(() => false);
  return storyQuestActive;
}

/**
 * Posts a branded Pyramida story (bot-rendered QR image) for `refKey` with the
 * user's referral caption + a tappable link widget. Shared by the in-app button
 * and the bot's "share to story" deep link. Returns false (no-op) when the
 * client can't share to stories or the story image URL isn't configured.
 *
 * The story link and QR carry the Story traffic source only while an active
 * story quest exists (or when `forceTagSource` says the caller IS such a
 * quest); otherwise they carry just the ref key.
 */
export async function shareReferralStory(
  refKey: number,
  strings: { caption: string; widgetName: string; prizeLabel?: string },
  bot: 'main' | 'team' = 'main',
  forceTagSource = false,
): Promise<boolean> {
  const tg = window.Telegram?.WebApp;
  if (!tg?.shareToStory || !canShareToStory()) return false;
  const tagSource = bot === 'main' && (forceTagSource || (await hasActiveStoryQuest()));
  const mediaUrl = buildStoryImageUrl(refKey, strings.prizeLabel, bot, tagSource);
  if (!mediaUrl) return false;
  const link =
    bot === 'team'
      ? buildTeamTelegramReferralLink(refKey)
      : HAS_REAL_BOT && tagSource
        ? buildPartnerTrackingLink(refKey, STORY_SHARE_SOURCE, { clickId: false })
        : buildReferralLink(refKey);
  tg.shareToStory(mediaUrl, {
    text: `${strings.caption}\n${link}`,
    widget_link: { url: link, name: strings.widgetName },
  });
  return true;
}

/**
 * Links the current Telegram user's chat to their wallet on the bot. No-op
 * (resolves false) unless enabled, inside Telegram, and given an address.
 * Best-effort: network/validation failures are swallowed so they never disrupt
 * the app.
 */
export type NotifyLinkResult = 'linked' | 'unauthorized' | 'retry' | 'skipped';

export async function linkTelegramNotifications(
  address: string,
  walletToken?: string,
): Promise<NotifyLinkResult> {
  // 'skipped' = linking is not applicable in this environment (not a Mini App).
  // Distinct from 'unauthorized' (a real rejection by the bot), which callers
  // may react to by dropping credentials.
  if (!NOTIFY_ENABLED || !isTelegramMiniApp() || !address) return 'skipped';
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return 'skipped';

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData, address, ...(walletToken ? { walletToken } : {}) }),
    });
    if (res.ok) return 'linked';
    return res.status === 400 || res.status === 401 || res.status === 403
      ? 'unauthorized'
      : 'retry';
  } catch {
    return 'retry';
  }
}

let appOpenReported = false;

/**
 * Tells the bot the user actually opened the Mini App, so it can measure the
 * /start -> first-open conversion and stop showing the new-user single-CTA
 * /start screen. Fires at most once per page load. No-op unless enabled and
 * inside Telegram. Best-effort: failures are swallowed.
 */
export async function reportAppOpened(): Promise<boolean> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp() || appOpenReported) return false;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return false;
  appOpenReported = true;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/app-opened`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Tells the bot the user GRANTED write access in the native
 * `requestWriteAccess()` prompt. The bot treats the grant as the equivalent of
 * pressing Start (it can DM the user now): `start` conversion + welcome
 * message. No-op unless enabled and inside Telegram. Best-effort.
 */
export async function reportWriteAccessGranted(): Promise<boolean> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return false;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return false;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/write-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

let lastSyncedLang: string | null = null;

/**
 * Tells the bot which UI language is active, so it DMs the user in it. No-op
 * unless enabled, inside Telegram, and given a language. De-duplicates repeats
 * of the same language. Best-effort: failures are swallowed.
 *
 * `chosen` marks an EXPLICIT user pick (the language dropdown) as opposed to an
 * auto-detected sync. Only an explicit pick sets `lang_chosen` on the bot side,
 * which is what suppresses the language picker on the bot / AI bot. An explicit
 * pick bypasses the de-dup so it always registers the choice.
 */
export async function syncTelegramLanguage(lang: string, chosen = false): Promise<boolean> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp() || !lang) return false;
  const base = lang.toLowerCase().split('-')[0];
  if (base === lastSyncedLang && !chosen) return false;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return false;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/lang`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData, lang: base, chosen }),
    });
    if (res.ok) lastSyncedLang = base;
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Asks the bot for the UI language stored for this Telegram user (the single
 * cross-surface source of truth — set when they chose a language in the bot, the
 * AI recruiter bot, or a previous app session). Returns the language code, or
 * null when disabled, outside Telegram, or none recorded. Best-effort: any
 * failure resolves null so it never blocks startup.
 */
export async function fetchStoredLanguage(): Promise<string | null> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return null;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return null;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/mylang`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { lang?: unknown };
    return typeof data.lang === 'string' && data.lang ? data.lang : null;
  } catch {
    return null;
  }
}

/** An active quest (task with a PRMD reward) plus this user's claim status. */
export interface QuestItem {
  id: string;
  code: string;
  type: string;
  titles: Record<string, string>;
  descriptions: Record<string, string>;
  rewardNano: string;
  channelUrl: string | null;
  minReferrals: number | null;
  minStarts: number | null;
  entryAddress: string | null;
  entryValueNano: string | null;
  /** ref_key: contract family behind entryAddress ('direct' | 'ec'). */
  entrySchema: string | null;
  /** Counted quests: this user's progress toward the goal (null elsewhere). */
  progressCount: number | null;
  claimStatus: string | null;
  /** True when this user can never earn the quest (e.g. already/previously
   *  subscribed for subscribe_wallet) — the card is hidden. */
  ineligible?: boolean;
}

/**
 * Fetches the active quests together with this user's claim status per quest.
 * Returns null when disabled, outside Telegram, or on any failure — the Tasks
 * screen then shows its "open inside Telegram" fallback.
 */
export async function fetchQuests(): Promise<QuestItem[] | null> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return null;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return null;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/quests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { quests?: unknown };
    return Array.isArray(data.quests) ? (data.quests as QuestItem[]) : null;
  } catch {
    return null;
  }
}

/**
 * Fetches the public quest catalog (no user context) — works both on the
 * plain website and inside Telegram. `claimStatus` is always null here.
 */
export async function fetchPublicQuests(): Promise<QuestItem[] | null> {
  if (!NOTIFY_ENABLED) return null;
  try {
    const res = await fetch(`${NOTIFY_API_BASE}/quests/public`);
    if (!res.ok) return null;
    const data = (await res.json()) as { quests?: unknown };
    return Array.isArray(data.quests) ? (data.quests as QuestItem[]) : null;
  } catch {
    return null;
  }
}

/**
 * A signed cumulative reward voucher for the QuestClaim contract: the total
 * PRMD this wallet has earned, which the user redeems himself (paying the
 * payout gas). The contract pays only the part not yet paid.
 */
export interface QuestVoucher {
  contract: string;
  /** 'qc' = QuestClaim (claim only), 'ec' = EntryClaim (entry + claim). */
  schema: 'qc' | 'ec';
  totalNano: string;
  claimableNano: string;
  validUntil: number;
  /** 64-byte Ed25519 signature, base64. */
  signature: string;
  valueNano: string;
}

/** One maturing reward row in the pending list. */
export interface PendingClaimItem {
  questId: string;
  amountNano: string;
  /** Unix seconds when this reward matures, null when unknown. */
  eligibleAt: number | null;
  /** True once matured — waiting on the contract for the user's claim. */
  ready?: boolean;
}

/** Rewards granted but still inside the review delay (not claimable yet). */
export interface PendingClaims {
  amountNano: string;
  /** Unix seconds when the earliest one matures, null when unknown. */
  eligibleAt: number | null;
  /** Unix seconds when that claim was granted — the maturity window's start. */
  startedAt: number | null;
  /** Every maturing reward, soonest first. */
  items?: PendingClaimItem[];
}

export interface VoucherState {
  voucher: QuestVoucher | null;
  pending: PendingClaims | null;
}

/**
 * Asks the bot for a fresh claim voucher plus the maturing (pending) tail.
 * Returns nulls when the wallet is not linked, claims are not configured, or
 * on any failure — the Rewards screen then shows no claim banner.
 */
export async function fetchQuestVoucher(): Promise<VoucherState> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return { voucher: null, pending: null };
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return { voucher: null, pending: null };

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/quests/voucher`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) return { voucher: null, pending: null };
    const data = (await res.json()) as {
      voucher?: QuestVoucher | null;
      pending?: PendingClaims | null;
    };
    return { voucher: data.voucher ?? null, pending: data.pending ?? null };
  } catch {
    return { voucher: null, pending: null };
  }
}

/**
 * Reports that the user shared a story for a story-type quest, creating a
 * pending claim that matures after the quest's review delay. Returns the
 * server's result ('created' | 'exists' | 'no_wallet' | 'not_found'), or null
 * on failure.
 */
export async function reportStoryQuestShared(questId: string): Promise<string | null> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return null;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return null;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/quests/story`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData, questId }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { result?: unknown };
    return typeof data.result === 'string' ? data.result : null;
  } catch {
    return null;
  }
}

/**
 * Tells the bot the wallet just SIGNED a claim transaction, so the "claim now"
 * prompt disappears immediately instead of waiting for on-chain settlement.
 * Fire-and-forget: failures only mean the prompt lingers a little longer.
 */
export async function reportRewardsClaimed(): Promise<void> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return;
  try {
    await fetch(`${NOTIFY_API_BASE}/quests/claimed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
  } catch {
    // Best-effort only.
  }
}

/**
 * Asks the bot for the referral key this Telegram user arrived with (stored
 * when they opened a `t.me/<bot>?start=<key>` deep link). Returns a non-negative
 * integer key (0 = owner), or null when disabled, outside Telegram, or none recorded.
 * Best-effort: any failure resolves null so it never blocks a purchase.
 */
export async function fetchTelegramReferrer(): Promise<number | null> {
  if (!NOTIFY_ENABLED || !isTelegramMiniApp()) return null;
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return null;

  try {
    const res = await fetch(`${NOTIFY_API_BASE}/referrer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { referrerKey?: unknown };
    const key = typeof data.referrerKey === 'number' ? data.referrerKey : null;
    return key !== null && Number.isInteger(key) && key >= 0
      ? sanitizeReferrerKey(key)
      : null;
  } catch {
    return null;
  }
}

/**
 * Resolves the referrer key to attach to an outgoing transaction, in priority
 * order: the key the page was opened with (start_param / URL / cookie), the
 * key the bot recorded for this Telegram user, and finally the key recorded
 * for the connected wallet's address (covers plain browser tabs with no
 * Telegram context). Null only when no attribution is known anywhere.
 */
export async function resolveOutgoingReferrerKey(
  address: string | null | undefined,
): Promise<number | null> {
  return (
    getIncomingReferrerKey() ??
    (await fetchTelegramReferrer()) ??
    (await fetchReferrerByAddress(address))
  );
}

/**
 * Asks the bot for the referral key recorded for the user whose proof-verified
 * wallet is this address — the fallback when the page has no Telegram context
 * (plain browser tab) or the per-user lookup yielded nothing. Getting the key
 * into the wallet's FIRST transaction matters: the contract permanently
 * aliases a missing referrer to the owner. Best-effort: failures resolve null.
 */
export async function fetchReferrerByAddress(
  address: string | null | undefined,
): Promise<number | null> {
  if (!NOTIFY_ENABLED || !address) return null;
  try {
    const res = await fetch(`${NOTIFY_API_BASE}/referrer-by-address`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { referrerKey?: unknown };
    const key = typeof data.referrerKey === 'number' ? data.referrerKey : null;
    return key !== null && Number.isInteger(key) && key >= 0
      ? sanitizeReferrerKey(key)
      : null;
  } catch {
    return null;
  }
}
